import { EventEmitter, useUserMSPCheckHook } from '@nile/components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import { useState } from 'react';

interface User {
  name: string;
  userId: string;
  avatarFileName: string;
}

interface ConcurrentViewer {
  user: User;
  state: 'ACTIVE' | 'INACTIVE' | string;
  isTyping: boolean;
}

type eventType =
  | 'UPDATE'
  | 'EXIT_PAGE'
  | 'REGISTER_PAGE'
  | 'UNREGISTER_PAGE'
  | 'TYPING_CONVERSATION'
  | 'UPDATE_CONVERSATION'
  | 'NOTIFY_COLLABORATORS_INFO'
  | 'STOPPED_TYPING_CONVERSATION';

interface PayloadType {
  event: eventType;
  page: {
    type: 'DETAIL';
    entity: 'Ticket';
    recordId: string;
  };
  epochMillis: string;
  type: 'SERVER_EVENT' | 'CLIENT_EVENT';
}

interface RequestPayloadType extends PayloadType {
  data: {
    allowSimultaneousPage: boolean;
  };
}

interface ResponsePayloadType extends PayloadType {
  user: User;
  data: {
    allowSimultaneousPage: boolean;
    collaborators: Concurrentviewer[];
  };
}

interface requestEventDataType {
  context: string;
  // windowsId: string;
  payload: RequestPayloadType;
}

interface responseEventDataType extends requestEventDataType {
  payload: ResponsePayloadType;
}

let broadcastChannel;
if (BroadcastChannel) {
  broadcastChannel = new BroadcastChannel('SuperShare');
}

export default function getTicketUpdates(
  ticketId: string
): {
  concurrentViewers: concurrentviewer[];
  sendClientEvent: (event: eventType, ticketId: string) => void;
} {
  const flags = useFlags();
  const isMSP = useUserMSPCheckHook();
  const [concurrentViewers, setConcurrentViewers] = useState([]);

  function sendEvent(event: eventType, ticketID: string) {
    const ticketIdRegistered = sessionStorage.getItem(ticketID);
    if (event === 'REGISTER_PAGE') {
      if (ticketIdRegistered === 'REGISTER_PAGE') {
        return;
      }
      sessionStorage.setItem(ticketID, 'REGISTER_PAGE');
    }

    if (
      (event === 'EXIT_PAGE' || event === 'UNREGISTER_PAGE') &&
      ticketIdRegistered
    ) {
      sessionStorage.removeItem(ticketID);
    }

    const postMessageData: requestEventDataType = {
      context: 'pageUpdate',
      // windowsId: ticketId,
      payload: {
        type: 'CLIENT_EVENT',
        page: {
          type: 'DETAIL',
          entity: 'Ticket',
          recordId: ticketID
        },
        data: {
          allowSimultaneousPage: true
        },
        event: event || 'REGISTER_PAGE',
        epochMillis: new Date().getTime().toString()
      }
    };
    if (isMSP) {
      broadcastChannel.postMessage(
        JSON.stringify({
          name: 'wsdata',
          data: postMessageData
        })
      );
    }
  }

  broadcastChannel.onmessage = (event) => {
    if (isMSP && event.data) {
      const eventData: responseEventDataType = JSON.parse(event.data);
      const { context, payload } = eventData;
      if (
        context === 'pageUpdate' &&
        payload?.page?.recordId === ticketId &&
        payload?.type === 'SERVER_EVENT'
      ) {
        const clonedConcurrentViewers = cloneDeep(concurrentViewers);
        switch (payload.event) {
          case 'UPDATE':
            if (payload?.page?.type === 'DETAIL') {
              EventEmitter.dispatch('notifyRefresh');
            }
            break;
          case 'NOTIFY_COLLABORATORS_INFO':
            setConcurrentViewers(payload.data.collaborators);
            break;
          case 'REGISTER_PAGE':
            const registeredUserIndex = clonedConcurrentViewers.findIndex(
              (viewer) => {
                return viewer.user.userId === payload.user?.userId;
              }
            );
            if (registeredUserIndex > -1) {
              clonedConcurrentViewers[registeredUserIndex].state = 'ACTIVE';
            } else {
              clonedConcurrentViewers.push({
                user: payload.user,
                state: 'ACTIVE',
                isTyping: false
              });
            }
            setConcurrentViewers(clonedConcurrentViewers);
            break;
          case 'EXIT_PAGE':
            setConcurrentViewers(
              clonedConcurrentViewers.map((currentViewer) => {
                if (currentViewer.user.userId === payload.user.userId) {
                  return {
                    ...currentViewer,
                    state: 'INACTIVE',
                    isTyping: false
                  };
                }
                return currentViewer;
              })
            );
            break;
          case 'UNREGISTER_PAGE':
            setConcurrentViewers(
              clonedConcurrentViewers.filter(
                (currentViewer) =>
                  currentViewer.user.userId !== payload.user.userId
              )
            );
            break;
          case 'TYPING_CONVERSATION':
            const userIndex = clonedConcurrentViewers?.findIndex((viewer) => {
              return viewer?.user?.userId === payload?.user?.userId;
            });
            if (
              userIndex > -1 &&
              !clonedConcurrentViewers[userIndex]?.isTyping
            ) {
              clonedConcurrentViewers[userIndex] = {
                ...clonedConcurrentViewers[userIndex],
                isTyping: true
              };
              setConcurrentViewers(clonedConcurrentViewers);
            }
            break;
          case 'STOPPED_TYPING_CONVERSATION':
            setConcurrentViewers(
              clonedConcurrentViewers.map((currentViewer) => {
                if (currentViewer.user.userId === payload.user.userId) {
                  return {
                    ...currentViewer,
                    isTyping: false
                  };
                }
                return currentViewer;
              })
            );
            break;
          default:
            break;
        }
      }
    }
  };

  return {
    concurrentViewers: concurrentViewers,
    sendClientEvent: sendEvent
  };
}
